import { useState, useRef, useEffect, useContext } from 'react';
import { Container } from '@mui/material';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import UserContext from '../../../../context/userContext';
import style from './AIChatPage.module.css';

type AgentKey = 'skriv' | 'hms' | 'it' | 'kvalitet' | 'historiker';

interface AgentDef {
    key: AgentKey;
    name: string;
    role: string;
    blurb: string;
    suggestion?: string;
}

const AGENTS: AgentDef[] = [
    {
        key: 'skriv',
        name: 'Assistenten',
        role: 'Tekst og kommunikasjon',
        blurb: 'Hjelp med e-post, intern kommunikasjon, møtesammendrag, nyhetsinnlegg og oversettelser.',
    },
    {
        key: 'hms',
        name: 'HMS-rådgiveren',
        role: 'Helse · Miljø · Sikkerhet',
        blurb: 'Avviksrapportering, vernerunder, brann, evakuering, førstehjelp og verneutstyr.',
        suggestion: 'Hva skal jeg gjøre hvis jeg observerer noe usikkert på linja?',
    },
    {
        key: 'it',
        name: 'IT-rådgiveren',
        role: 'Teknisk hjelp',
        blurb: 'Passord, e-post, Microsoft 365, Teams, VPN og skriverproblemer.',
        suggestion: 'Hvordan kobler jeg på VPN hjemmefra?',
    },
    {
        key: 'kvalitet',
        name: 'Kvalitetsrådgiveren',
        role: 'Fabrikken',
        blurb: 'Produksjonsrutiner, ISO 22000, sporbarhet, råvarer og produkter.',
        suggestion: 'Hva er sporbarhetskravene for et batch Stupedama?',
    },
    {
        key: 'historiker',
        name: 'Historikeren',
        role: 'Brynild siden 1895',
        blurb: 'Brynilds historie, produktarven, og merkevaren gjennom tidene.',
        suggestion: 'Når kom Stupedama på markedet?',
    },
];

interface ChatMessage { role: 'user' | 'assistant'; content: string }

function getCSRFToken(): string {
    const m = document.cookie.match(/csrftoken=([\w-]+)/);
    return m ? m[1] : '';
}

export default function AIChatPage() {
    const { userData } = useContext(UserContext);
    const firstName = userData?.user?.firstname ?? 'kollega';
    const [agent, setAgent] = useState<AgentKey>('skriv');
    const [threads, setThreads] = useState<Record<AgentKey, ChatMessage[]>>({
        skriv: [], hms: [], it: [], kvalitet: [], historiker: [],
    });
    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const threadEndRef = useRef<HTMLDivElement | null>(null);

    const currentMessages = threads[agent];
    const currentAgent = AGENTS.find((a) => a.key === agent)!;

    useEffect(() => {
        if (!threadEndRef.current) return;
        // Only auto-scroll if user is already near the bottom — don't fight them
        // when they've scrolled up to read earlier content. `behavior: auto` (instant)
        // is far cheaper than `smooth` when called once per streamed token.
        const nearBottom =
            window.innerHeight + window.scrollY >= document.body.scrollHeight - 240;
        if (nearBottom) {
            threadEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
    }, [currentMessages]);

    useEffect(() => {
        return () => { abortRef.current?.abort(); };
    }, []);

    async function send(text: string) {
        const trimmed = text.trim();
        if (!trimmed || streaming) return;

        setError(null);
        setInput('');

        const userMsg: ChatMessage = { role: 'user', content: trimmed };
        const newHistory = [...currentMessages, userMsg];
        // optimistic: user message + empty assistant placeholder we'll fill
        setThreads((t) => ({ ...t, [agent]: [...newHistory, { role: 'assistant', content: '' }] }));
        setStreaming(true);

        const ctrl = new AbortController();
        abortRef.current = ctrl;

        try {
            const resp = await fetch('/intranet/ai/chat', {
                method: 'POST',
                signal: ctrl.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCSRFToken(),
                },
                body: JSON.stringify({ agent, messages: newHistory }),
                credentials: 'same-origin',
            });

            if (!resp.ok || !resp.body) {
                const txt = await resp.text().catch(() => 'ukjent feil');
                throw new Error(`${resp.status}: ${txt.slice(0, 200)}`);
            }

            const reader = resp.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let assistantText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                // Parse SSE: events separated by \n\n, each starts with "data: "
                let sep;
                while ((sep = buffer.indexOf('\n\n')) !== -1) {
                    const chunk = buffer.slice(0, sep);
                    buffer = buffer.slice(sep + 2);
                    if (!chunk.startsWith('data: ')) continue;
                    const json = chunk.slice(6);
                    try {
                        const evt = JSON.parse(json);
                        if (evt.text) {
                            assistantText += evt.text;
                            // update last message (the assistant placeholder)
                            setThreads((t) => {
                                const arr = [...t[agent]];
                                arr[arr.length - 1] = { role: 'assistant', content: assistantText };
                                return { ...t, [agent]: arr };
                            });
                        } else if (evt.error) {
                            setError(evt.error);
                        }
                    } catch {
                        /* ignore parse errors on partial events */
                    }
                }
            }
        } catch (e: any) {
            if (e.name !== 'AbortError') {
                setError(e?.message ?? 'Klarte ikke å hente svar.');
            }
        } finally {
            setStreaming(false);
            abortRef.current = null;
            // remove empty assistant placeholder if streaming yielded nothing
            setThreads((t) => {
                const arr = t[agent];
                if (arr.length > 0 && arr[arr.length - 1].role === 'assistant' && arr[arr.length - 1].content === '') {
                    return { ...t, [agent]: arr.slice(0, -1) };
                }
                return t;
            });
        }
    }

    function clearThread() {
        setThreads((t) => ({ ...t, [agent]: [] }));
        setError(null);
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send(input);
        }
    }

    return (
        <Container maxWidth={false} className={style.page}>
            <motion.header
                className={style.pageHead}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
            >
                <h1 className={style.pageTitle}>
                    <span className={style.pageTitleItalic}>KI</span>
                </h1>
                {currentMessages.length > 0 && (
                    <button className={style.clearBtn} onClick={clearThread} disabled={streaming}>
                        Ny samtale
                    </button>
                )}
            </motion.header>

            <div className={style.headRule} />

            <nav className={style.tabs} aria-label="Velg KI-rådgiver">
                {AGENTS.map((a) => (
                    <button
                        key={a.key}
                        className={`${style.tab} ${a.key === agent ? style.tabActive : ''}`}
                        onClick={() => setAgent(a.key)}
                        disabled={streaming && a.key !== agent}
                    >
                        <span className={style.tabKicker}>{a.role}</span>
                        <span className={style.tabName}>{a.name}</span>
                    </button>
                ))}
            </nav>

            <div className={style.agentBlurb}>
                <span className={style.blurbKicker}>Om denne</span>
                <span>{currentAgent.blurb}</span>
            </div>

            <section className={style.thread}>
                {currentMessages.length === 0 ? (
                    <div className={style.empty}>
                        <div className={style.emptyKicker}>Hei {firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()}</div>
                        <div className={style.emptyTitle}>
                            Hva vil du spørre <span className={style.emptyTitleItalic}>{currentAgent.name.toLowerCase()}</span> om?
                        </div>
                        {currentAgent.suggestion && (
                            <button
                                className={style.suggestion}
                                onClick={() => send(currentAgent.suggestion!)}
                                disabled={streaming}
                            >
                                <span className={style.suggestionKicker}>Foreslag</span>
                                <span className={style.suggestionText}>{currentAgent.suggestion}</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {currentMessages.map((m, i) => (
                            <article key={i} className={`${style.msg} ${style[`msg_${m.role}`]}`}>
                                <div className={style.msgKicker}>
                                    {m.role === 'user'
                                        ? 'Du'
                                        : currentAgent.name}
                                </div>
                                <div className={style.msgBody}>
                                    {m.content
                                        ? (m.role === 'assistant'
                                            ? <ReactMarkdown>{m.content}</ReactMarkdown>
                                            : m.content)
                                        : (streaming && i === currentMessages.length - 1
                                            ? <span className={style.typingDot} />
                                            : null)}
                                </div>
                            </article>
                        ))}
                        <div ref={threadEndRef} />
                    </>
                )}

                {error && (
                    <div className={style.errorBanner}>
                        <span className={style.errorKicker}>Feil</span>
                        <span>{error}</span>
                    </div>
                )}
            </section>

            <form
                className={style.composer}
                onSubmit={(e) => {
                    e.preventDefault();
                    send(input);
                }}
            >
                <textarea
                    className={style.input}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={`Skriv til ${currentAgent.name.toLowerCase()}…`}
                    rows={2}
                    disabled={streaming}
                />
                <div className={style.composerMeta}>
                    <span className={style.composerHint}>Enter for å sende · Shift+Enter for ny linje</span>
                    <button
                        type="submit"
                        className={style.sendBtn}
                        disabled={streaming || !input.trim()}
                    >
                        {streaming ? 'Skriver…' : 'Send'}
                        {!streaming && <span className={style.btnArrow}>→</span>}
                    </button>
                </div>
            </form>

        </Container>
    );
}
